<template>
  <div class="relative min-h-[720px] w-full pb-10">
    <div
      v-if="accessLoading || !advertisingAccess.unlocked"
      class="fixed bottom-0 left-0 right-0 top-[52px] z-[2500] flex items-center justify-center bg-white/35 px-4 py-5 backdrop-blur-[4px] lg:left-[280px]"
      role="region"
      aria-label="Advertising access"
    >
      <div v-if="accessLoading" class="mt-24 flex items-center gap-3 rounded-full border border-white/80 bg-white/95 px-5 py-3 text-sm font-semibold text-zinc-700 shadow-xl">
        <span class="h-4 w-4 animate-spin rounded-full border-2 border-zinc-200 border-t-[#2D8CFF]" />
        Checking Advertising access…
      </div>
      <article v-else class="grid max-h-[calc(100vh-92px)] w-full max-w-4xl overflow-auto rounded-[26px] border border-white/80 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.22)] lg:grid-cols-[1.12fr_0.88fr] lg:overflow-hidden">
        <div class="p-6 sm:p-8 lg:p-9">
          <div class="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
            <CurrencyDollarIcon class="h-4 w-4" />
            Monetize · Advertising
          </div>
          <h2 class="mt-4 max-w-xl text-3xl font-bold tracking-tight text-zinc-950">
            Turn your audience into sponsor revenue.
          </h2>
          <p class="mt-3 max-w-2xl text-sm leading-6 text-zinc-600">
            Accept sponsors and manage ad inventory directly from one workspace. Brands can buy advertising slots in your membership and run campaigns from their own Advertising account while you stay in control and get paid.
          </p>

          <div class="mt-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
            <p class="text-sm font-semibold text-blue-950">{{ accessRequirementTitle }}</p>
            <p class="mt-1 text-sm leading-6 text-blue-800">{{ accessRequirementDetail }}</p>
          </div>

          <button type="button" class="mt-5 inline-flex h-11 items-center justify-center rounded-xl bg-[#2D8CFF] px-5 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition hover:-translate-y-0.5 hover:bg-[#2377d9]" @click="openBilling">
            {{ advertisingAccess.onRequiredPlan ? 'Review billing' : `Upgrade to ${unlockPlanName}` }}
            <ChevronRightIcon class="ml-2 h-4 w-4" />
          </button>

          <div class="mt-6 grid gap-4 border-t border-zinc-100 pt-5 sm:grid-cols-3">
            <div><span class="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-950 text-xs font-bold text-white">1</span><p class="mt-3 text-sm font-semibold text-zinc-900">Offer inventory</p><p class="mt-1 text-xs leading-5 text-zinc-500">Open placements across your membership.</p></div>
            <div><span class="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-950 text-xs font-bold text-white">2</span><p class="mt-3 text-sm font-semibold text-zinc-900">Approve brands</p><p class="mt-1 text-xs leading-5 text-zinc-500">Stay in control of every sponsor and campaign.</p></div>
            <div><span class="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-950 text-xs font-bold text-white">3</span><p class="mt-3 text-sm font-semibold text-zinc-900">Get paid</p><p class="mt-1 text-xs leading-5 text-zinc-500">Earn directly from advertising demand.</p></div>
          </div>
        </div>

        <div class="relative flex min-h-[300px] items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-violet-100 p-6 lg:min-h-full">
          <div class="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-blue-300/30 blur-3xl" />
          <div class="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-violet-300/30 blur-3xl" />
          <div class="relative w-full max-w-xs rounded-[24px] border-[10px] border-white bg-zinc-950 p-4 shadow-2xl">
            <div class="flex items-center justify-between">
              <span class="rounded-full bg-rose-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">Sponsor</span>
              <span class="text-xs font-semibold text-zinc-400">Brand account</span>
            </div>
            <div class="mt-4 overflow-hidden rounded-2xl bg-gradient-to-br from-[#2D8CFF] via-blue-600 to-violet-700 p-5 text-white">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-blue-100">Featured partner</p>
              <p class="mt-2 text-xl font-bold">Campaigns your audience will notice.</p>
              <div class="mt-8 h-2 w-3/4 rounded-full bg-white/60" />
              <div class="mt-2 h-2 w-1/2 rounded-full bg-white/30" />
            </div>
            <div class="mt-4 flex items-center justify-between rounded-xl bg-white/10 p-3">
              <div><p class="text-xs font-semibold text-white">Your ad inventory</p><p class="mt-1 text-[10px] text-zinc-400">Managed in Platforma</p></div>
              <span class="rounded-lg bg-white px-3 py-2 text-[10px] font-bold text-zinc-950">Get paid</span>
            </div>
          </div>
        </div>
      </article>
    </div>
    <div class="flex flex-col gap-4 border-b border-zinc-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-400">
          <span>Revenue</span>
          <ChevronRightIcon class="h-3.5 w-3.5" />
          <span class="text-[#2D8CFF]">Advertising</span>
        </div>
        <h1 class="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">
          Advertising
        </h1>
        <p class="mt-1 max-w-2xl text-sm text-zinc-500">
          Create, review, and optimize banner and video campaigns across your member experience.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 text-sm font-semibold text-zinc-700 shadow-sm transition hover:bg-zinc-50"
        >
          <ArrowDownTrayIcon class="h-4 w-4" />
          Export
        </button>
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lg bg-[#2D8CFF] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f78e5]"
          @click="openCampaignBuilder"
        >
          <PlusIcon class="h-5 w-5" />
          New campaign
        </button>
      </div>
    </div>

    <div class="border-b border-zinc-200">
      <nav class="-mb-px flex gap-7 overflow-x-auto" aria-label="Advertising sections">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="relative flex shrink-0 items-center gap-2 border-b-2 px-0.5 py-4 text-sm font-medium transition"
          :class="activeTab === tab.id
            ? 'border-[#2D8CFF] text-[#2D8CFF]'
            : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-800'"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
          <span
            v-if="tab.id === 'approvals' ? approvals.length : tab.count"
            class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
            :class="activeTab === tab.id ? 'bg-blue-50 text-[#2D8CFF]' : 'bg-zinc-100 text-zinc-500'"
          >
            {{ tab.id === 'approvals' ? approvals.length : tab.count }}
          </span>
        </button>
      </nav>
    </div>

    <section v-if="activeTab === 'overview'" class="pt-6">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="metric in overviewMetrics"
          :key="metric.label"
          class="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                {{ metric.label }}
              </p>
              <p class="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">
                {{ metric.value }}
              </p>
            </div>
            <div class="rounded-lg bg-blue-50 p-2 text-[#2D8CFF]">
              <component :is="metric.icon" class="h-5 w-5" />
            </div>
          </div>
          <p class="mt-3 text-xs text-zinc-500">
            <span :class="metric.positive ? 'text-emerald-600' : 'text-amber-600'" class="font-semibold">
              {{ metric.change }}
            </span>
            {{ metric.context }}
          </p>
        </article>
      </div>

      <div class="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(300px,0.75fr)]">
        <article class="rounded-xl border border-zinc-200 bg-white shadow-sm">
          <div class="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
            <div>
              <h2 class="text-sm font-semibold text-zinc-900">
                Campaign performance
              </h2>
              <p class="mt-1 text-xs text-zinc-500">
                Impressions and clicks across all active campaigns
              </p>
            </div>
            <select class="h-9 rounded-lg border-zinc-200 bg-white py-1.5 pl-3 pr-8 text-xs font-medium text-zinc-700 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
              <option>Last 30 days</option>
              <option>Last 7 days</option>
              <option>This quarter</option>
            </select>
          </div>
          <div class="p-5">
            <div class="mb-4 flex items-center gap-5 text-xs text-zinc-500">
              <span class="inline-flex items-center gap-2"><span class="h-2.5 w-2.5 rounded-full bg-[#2D8CFF]" /> Impressions</span>
              <span class="inline-flex items-center gap-2"><span class="h-2.5 w-2.5 rounded-full bg-violet-500" /> Clicks</span>
            </div>
            <div class="relative h-64 w-full overflow-hidden rounded-lg bg-gradient-to-b from-blue-50/60 to-white">
              <div class="absolute inset-x-0 top-1/4 border-t border-dashed border-zinc-200" />
              <div class="absolute inset-x-0 top-2/4 border-t border-dashed border-zinc-200" />
              <div class="absolute inset-x-0 top-3/4 border-t border-dashed border-zinc-200" />
              <svg viewBox="0 0 800 250" class="absolute inset-0 h-full w-full" preserveAspectRatio="none" aria-label="Campaign performance trend">
                <defs>
                  <linearGradient id="adPerformanceFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stop-color="#2D8CFF" stop-opacity="0.22" />
                    <stop offset="100%" stop-color="#2D8CFF" stop-opacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0 210 C55 198 75 174 125 182 S210 142 264 154 S342 112 405 126 S486 74 548 96 S628 50 684 65 S756 38 800 28 L800 250 L0 250 Z" fill="url(#adPerformanceFill)" />
                <path d="M0 210 C55 198 75 174 125 182 S210 142 264 154 S342 112 405 126 S486 74 548 96 S628 50 684 65 S756 38 800 28" fill="none" stroke="#2D8CFF" stroke-width="4" stroke-linecap="round" />
                <path d="M0 225 C62 218 92 207 145 212 S225 190 286 198 S360 172 421 180 S507 145 562 158 S645 127 704 138 S765 112 800 116" fill="none" stroke="#8B5CF6" stroke-width="3" stroke-linecap="round" stroke-dasharray="7 7" />
              </svg>
              <div class="absolute inset-x-3 bottom-2 flex justify-between text-[10px] font-medium text-zinc-400">
                <span>Jul 20</span><span>Jul 27</span><span>Aug 3</span><span>Aug 10</span><span>Aug 19</span>
              </div>
            </div>
          </div>
        </article>

        <article class="rounded-xl border border-zinc-200 bg-white shadow-sm">
          <div class="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
            <div>
              <h2 class="text-sm font-semibold text-zinc-900">
                Needs your attention
              </h2>
              <p class="mt-1 text-xs text-zinc-500">
                Campaigns awaiting action
              </p>
            </div>
            <span class="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">3 items</span>
          </div>
          <div class="divide-y divide-zinc-100">
            <button
              v-for="item in attentionItems"
              :key="item.title"
              type="button"
              class="flex w-full items-start gap-3 px-5 py-4 text-left transition hover:bg-zinc-50"
              @click="activeTab = item.tab"
            >
              <span class="mt-0.5 rounded-lg p-2" :class="item.iconClass">
                <component :is="item.icon" class="h-4 w-4" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium text-zinc-900">{{ item.title }}</span>
                <span class="mt-0.5 block text-xs leading-5 text-zinc-500">{{ item.detail }}</span>
              </span>
              <ChevronRightIcon class="mt-2 h-4 w-4 text-zinc-400" />
            </button>
          </div>
        </article>
      </div>

      <article class="mt-5 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div class="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
          <div>
            <h2 class="text-sm font-semibold text-zinc-900">Top campaigns</h2>
            <p class="mt-1 text-xs text-zinc-500">Ranked by clicks during the selected period</p>
          </div>
          <button type="button" class="text-xs font-semibold text-[#2D8CFF] hover:text-blue-700" @click="activeTab = 'campaigns'">
            View all campaigns
          </button>
        </div>
        <CampaignTable :campaigns="campaigns.slice(0, 4)" compact @toggle="toggleCampaign" />
      </article>
    </section>

    <section v-else-if="activeTab === 'campaigns'" class="pt-6">
      <div class="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div class="flex flex-1 flex-wrap items-center gap-2">
          <div class="relative min-w-[240px] max-w-md flex-1">
            <MagnifyingGlassIcon class="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-zinc-400" />
            <input
              v-model="campaignSearch"
              type="search"
              placeholder="Search campaigns"
              class="h-10 w-full rounded-lg border-zinc-300 bg-white pl-10 pr-3 text-sm text-zinc-800 placeholder:text-zinc-400 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]"
            >
          </div>
          <button type="button" class="inline-flex h-10 items-center gap-2 rounded-lg border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            <FunnelIcon class="h-4 w-4" /> Filters
          </button>
          <select v-model="campaignStatusFilter" class="h-10 rounded-lg border-zinc-300 bg-white py-1.5 pl-3 pr-8 text-sm text-zinc-700 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
            <option value="all">All statuses</option>
            <option value="Active">Active</option>
            <option value="In review">In review</option>
            <option value="Paused">Paused</option>
            <option value="Draft">Draft</option>
          </select>
        </div>
        <p class="text-xs text-zinc-500">
          {{ filteredCampaigns.length }} campaigns · Updated just now
        </p>
      </div>

      <article class="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <CampaignTable :campaigns="filteredCampaigns" selectable @toggle="toggleCampaign" />
        <div class="flex flex-col gap-2 border-t border-zinc-200 px-5 py-3 text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
          <span>Showing 1–{{ filteredCampaigns.length }} of {{ filteredCampaigns.length }}</span>
          <div class="flex gap-1">
            <button type="button" class="rounded-md border border-zinc-200 px-3 py-1.5 text-zinc-400">Previous</button>
            <button type="button" class="rounded-md border border-zinc-200 px-3 py-1.5 font-medium text-zinc-700 hover:bg-zinc-50">Next</button>
          </div>
        </div>
      </article>
    </section>

    <section v-else-if="activeTab === 'approvals'" class="pt-6">
      <div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-zinc-900">Campaign approvals</h2>
          <p class="mt-1 text-sm text-zinc-500">Review creative, targeting, and destinations before ads go live.</p>
        </div>
        <div class="inline-flex self-start rounded-lg bg-zinc-100 p-1 text-xs font-semibold text-zinc-600">
          <button type="button" class="rounded-md bg-white px-3 py-2 text-zinc-900 shadow-sm">Needs review (3)</button>
          <button type="button" class="rounded-md px-3 py-2 hover:text-zinc-900">History</button>
        </div>
      </div>

      <div class="space-y-4">
        <article
          v-for="approval in approvals"
          :key="approval.id"
          class="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm"
        >
          <div class="grid lg:grid-cols-[280px_minmax(0,1fr)_210px]">
            <div class="flex min-h-44 items-center justify-center bg-zinc-950 p-5">
              <div v-if="approval.format === 'Video'" class="relative aspect-video w-full overflow-hidden rounded-lg bg-gradient-to-br from-indigo-500 via-violet-600 to-fuchsia-600 shadow-lg">
                <div class="absolute inset-0 bg-black/15" />
                <div class="absolute inset-0 flex items-center justify-center">
                  <span class="rounded-full bg-white/90 p-3 text-violet-700 shadow-lg"><PlayIcon class="h-6 w-6" /></span>
                </div>
                <span class="absolute bottom-2 right-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">0:15</span>
              </div>
              <div v-else class="relative aspect-[5/2] w-full overflow-hidden rounded-lg bg-gradient-to-r from-blue-600 to-cyan-400 p-4 text-white shadow-lg">
                <span class="text-[10px] font-semibold uppercase tracking-widest text-white/75">{{ approval.brand }}</span>
                <p class="mt-1 text-lg font-bold leading-tight">{{ approval.headline }}</p>
                <span class="absolute bottom-3 right-3 rounded bg-white px-2 py-1 text-[9px] font-bold text-blue-700">LEARN MORE</span>
              </div>
            </div>

            <div class="p-5">
              <div class="flex flex-wrap items-center gap-2">
                <span class="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">Awaiting review</span>
                <span class="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-600">{{ approval.format }}</span>
                <span class="text-xs text-zinc-400">Submitted {{ approval.submitted }}</span>
              </div>
              <h3 class="mt-3 text-base font-semibold text-zinc-900">{{ approval.name }}</h3>
              <p class="mt-1 text-sm text-zinc-500">{{ approval.advertiser }}</p>
              <dl class="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-xs sm:grid-cols-3">
                <div><dt class="text-zinc-400">Placement</dt><dd class="mt-1 font-medium text-zinc-700">{{ approval.placement }}</dd></div>
                <div><dt class="text-zinc-400">Audience</dt><dd class="mt-1 font-medium text-zinc-700">{{ approval.audience }}</dd></div>
                <div><dt class="text-zinc-400">Budget</dt><dd class="mt-1 font-medium text-zinc-700">{{ approval.budget }}</dd></div>
              </dl>
            </div>

            <div class="flex flex-row items-center gap-2 border-t border-zinc-100 bg-zinc-50 p-5 lg:flex-col lg:justify-center lg:border-l lg:border-t-0">
              <button type="button" class="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700 lg:w-full lg:flex-none" @click="approveCampaign(approval.id)">
                <CheckCircleIcon class="h-4 w-4" /> Approve
              </button>
              <button type="button" class="h-9 flex-1 rounded-lg border border-zinc-300 bg-white px-3 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 lg:w-full lg:flex-none" @click="requestChanges(approval.id)">
                Request changes
              </button>
              <button type="button" class="h-9 rounded-lg px-3 text-xs font-semibold text-zinc-500 hover:text-zinc-900">View details</button>
            </div>
          </div>
        </article>

        <div v-if="!approvals.length" class="rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-16 text-center">
          <CheckCircleIcon class="mx-auto h-10 w-10 text-emerald-500" />
          <h3 class="mt-3 text-sm font-semibold text-zinc-900">All caught up</h3>
          <p class="mt-1 text-sm text-zinc-500">There are no campaigns waiting for review.</p>
        </div>
      </div>
    </section>

    <section v-else-if="activeTab === 'creatives'" class="pt-6">
      <div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-zinc-900">Creative library</h2>
          <p class="mt-1 text-sm text-zinc-500">Reusable banner and video assets for your campaigns.</p>
        </div>
        <button type="button" class="inline-flex h-10 self-start items-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 text-sm font-semibold text-zinc-700 hover:bg-zinc-50">
          <PlusIcon class="h-4 w-4" /> Upload creative
        </button>
      </div>
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <article v-for="creative in creatives" :key="creative.name" class="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
          <div class="relative flex aspect-video items-center justify-center p-6" :class="creative.previewClass">
            <div v-if="creative.type === 'Video'" class="absolute inset-0 flex items-center justify-center bg-black/10">
              <span class="rounded-full bg-white/90 p-3 shadow"><PlayIcon class="h-6 w-6 text-zinc-800" /></span>
            </div>
            <div v-else class="w-full rounded-md border border-white/30 bg-white/10 p-4 text-white backdrop-blur-sm">
              <p class="text-[10px] font-semibold uppercase tracking-widest opacity-75">PLATFORMA PARTNER</p>
              <p class="mt-1 text-xl font-bold">{{ creative.previewTitle }}</p>
            </div>
            <span class="absolute left-3 top-3 rounded bg-zinc-950/70 px-2 py-1 text-[10px] font-semibold text-white">{{ creative.size }}</span>
          </div>
          <div class="p-4">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <h3 class="truncate text-sm font-semibold text-zinc-900">{{ creative.name }}</h3>
                <p class="mt-1 text-xs text-zinc-500">{{ creative.type }} · Used in {{ creative.used }} campaigns</p>
              </div>
              <button type="button" class="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><EllipsisHorizontalIcon class="h-5 w-5" /></button>
            </div>
            <div class="mt-4 flex items-center justify-between border-t border-zinc-100 pt-3 text-xs">
              <span class="text-zinc-500">CTR <strong class="font-semibold text-zinc-800">{{ creative.ctr }}</strong></span>
              <span class="font-medium text-emerald-600">Approved</span>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section v-else class="pt-6">
      <div class="mb-5">
        <h2 class="text-lg font-semibold text-zinc-900">Ad placements</h2>
        <p class="mt-1 text-sm text-zinc-500">Control where advertising inventory appears inside member accounts.</p>
      </div>
      <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div class="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
          <div class="grid grid-cols-[minmax(0,1fr)_90px_110px] border-b border-zinc-200 bg-zinc-50 px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            <span>Placement</span><span>Status</span><span class="text-right">Fill rate</span>
          </div>
          <div class="divide-y divide-zinc-100">
            <div v-for="placement in placements" :key="placement.name" class="grid grid-cols-[minmax(0,1fr)_90px_110px] items-center gap-2 px-5 py-4">
              <div class="flex min-w-0 items-start gap-3">
                <span class="rounded-lg bg-blue-50 p-2 text-[#2D8CFF]"><component :is="placement.icon" class="h-5 w-5" /></span>
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-zinc-900">{{ placement.name }}</p>
                  <p class="mt-0.5 truncate text-xs text-zinc-500">{{ placement.detail }}</p>
                </div>
              </div>
              <button type="button" class="relative h-5 w-9 rounded-full transition" :class="placement.enabled ? 'bg-[#2D8CFF]' : 'bg-zinc-300'" @click="placement.enabled = !placement.enabled">
                <span class="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition" :class="placement.enabled ? 'left-[18px]' : 'left-0.5'" />
                <span class="sr-only">Toggle {{ placement.name }}</span>
              </button>
              <span class="text-right text-sm font-semibold text-zinc-800">{{ placement.fillRate }}</span>
            </div>
          </div>
        </div>

        <aside class="space-y-4">
          <div class="rounded-xl bg-zinc-950 p-5 text-white shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-wide text-zinc-400">Estimated inventory</p>
            <p class="mt-2 text-3xl font-semibold">248K</p>
            <p class="mt-1 text-xs text-zinc-400">available impressions / month</p>
            <div class="mt-5 h-2 overflow-hidden rounded-full bg-white/10">
              <div class="h-full w-[68%] rounded-full bg-[#2D8CFF]" />
            </div>
            <div class="mt-2 flex justify-between text-[11px] text-zinc-400"><span>68% reserved</span><span>32% available</span></div>
          </div>
          <div class="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
            <h3 class="text-sm font-semibold text-zinc-900">Member experience guardrails</h3>
            <label class="mt-4 block text-xs font-medium text-zinc-600">Maximum ads per session</label>
            <select class="mt-1.5 h-10 w-full rounded-lg border-zinc-300 text-sm text-zinc-700 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]"><option>3 ads</option><option>5 ads</option><option>8 ads</option></select>
            <label class="mt-4 flex items-start gap-3 text-xs leading-5 text-zinc-600">
              <input type="checkbox" checked class="mt-0.5 rounded border-zinc-300 text-[#2D8CFF] focus:ring-[#2D8CFF]">
              Do not show ads to paid plan members
            </label>
          </div>
        </aside>
      </div>
    </section>

    <div v-if="isCampaignBuilderOpen" class="fixed inset-0 z-[2600] flex justify-end" role="dialog" aria-modal="true" aria-labelledby="campaign-builder-title">
      <button type="button" class="absolute inset-0 bg-zinc-950/45 backdrop-blur-[1px]" aria-label="Close campaign builder" @click="closeCampaignBuilder" />
      <div class="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        <div class="flex items-start justify-between border-b border-zinc-200 px-6 py-5">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.15em] text-[#2D8CFF]">Step {{ builderStep }} of 3</p>
            <h2 id="campaign-builder-title" class="mt-1 text-xl font-semibold text-zinc-900">Create a campaign</h2>
          </div>
          <button type="button" class="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700" @click="closeCampaignBuilder"><XMarkIcon class="h-5 w-5" /></button>
        </div>
        <div class="grid grid-cols-3 border-b border-zinc-200 bg-zinc-50 px-6">
          <div v-for="(step, index) in builderSteps" :key="step" class="relative py-3 text-center text-xs font-semibold" :class="builderStep >= index + 1 ? 'text-[#2D8CFF]' : 'text-zinc-400'">
            {{ step }}
            <span v-if="builderStep === index + 1" class="absolute inset-x-0 bottom-0 h-0.5 bg-[#2D8CFF]" />
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="builderStep === 1">
            <h3 class="text-base font-semibold text-zinc-900">What is your campaign goal?</h3>
            <p class="mt-1 text-sm text-zinc-500">Platforma will use this to recommend placements and reporting.</p>
            <div class="mt-5 grid gap-3 sm:grid-cols-2">
              <button v-for="goal in goals" :key="goal.id" type="button" class="rounded-xl border p-4 text-left transition" :class="campaignDraft.goal === goal.id ? 'border-[#2D8CFF] bg-blue-50/60 ring-1 ring-[#2D8CFF]' : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'" @click="campaignDraft.goal = goal.id">
                <component :is="goal.icon" class="h-6 w-6" :class="campaignDraft.goal === goal.id ? 'text-[#2D8CFF]' : 'text-zinc-500'" />
                <p class="mt-3 text-sm font-semibold text-zinc-900">{{ goal.label }}</p>
                <p class="mt-1 text-xs leading-5 text-zinc-500">{{ goal.detail }}</p>
              </button>
            </div>
            <label class="mt-6 block">
              <span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Campaign name</span>
              <input v-model="campaignDraft.name" type="text" class="mt-1.5 h-11 w-full rounded-lg border-zinc-300 text-sm text-zinc-800 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]" placeholder="e.g. Summer launch · Member home">
            </label>
          </div>

          <div v-else-if="builderStep === 2">
            <h3 class="text-base font-semibold text-zinc-900">Choose a format and placement</h3>
            <p class="mt-1 text-sm text-zinc-500">Select how members will see this campaign.</p>
            <div class="mt-5 grid gap-3 sm:grid-cols-2">
              <button type="button" class="rounded-xl border p-5 text-left transition" :class="campaignDraft.format === 'Banner' ? 'border-[#2D8CFF] bg-blue-50/60 ring-1 ring-[#2D8CFF]' : 'border-zinc-200'" @click="campaignDraft.format = 'Banner'">
                <PhotoIcon class="h-7 w-7 text-[#2D8CFF]" /><p class="mt-3 text-sm font-semibold text-zinc-900">Banner ad</p><p class="mt-1 text-xs text-zinc-500">Static or animated image with a destination link.</p>
              </button>
              <button type="button" class="rounded-xl border p-5 text-left transition" :class="campaignDraft.format === 'Video' ? 'border-[#2D8CFF] bg-blue-50/60 ring-1 ring-[#2D8CFF]' : 'border-zinc-200'" @click="campaignDraft.format = 'Video'">
                <VideoCameraIcon class="h-7 w-7 text-violet-600" /><p class="mt-3 text-sm font-semibold text-zinc-900">Video ad</p><p class="mt-1 text-xs text-zinc-500">In-feed or pre-roll video up to 30 seconds.</p>
              </button>
            </div>
            <label class="mt-6 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Placement</label>
            <select v-model="campaignDraft.placement" class="mt-1.5 h-11 w-full rounded-lg border-zinc-300 text-sm text-zinc-800 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
              <option>Member dashboard</option><option>Newsfeed · In-feed</option><option>Video channel · Pre-roll</option><option>Course lesson · Sidebar</option>
            </select>
            <div class="mt-6 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center">
              <ArrowDownTrayIcon class="mx-auto h-7 w-7 text-zinc-400" />
              <p class="mt-2 text-sm font-semibold text-zinc-800">Drop your creative here</p>
              <p class="mt-1 text-xs text-zinc-500">or choose an approved asset from the Creative library</p>
              <button type="button" class="mt-3 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-700">Choose creative</button>
            </div>
          </div>

          <div v-else>
            <h3 class="text-base font-semibold text-zinc-900">Audience, budget, and review</h3>
            <p class="mt-1 text-sm text-zinc-500">Define delivery, then submit the campaign for approval.</p>
            <div class="mt-5 grid gap-4 sm:grid-cols-2">
              <label><span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Audience</span><select v-model="campaignDraft.audience" class="mt-1.5 h-11 w-full rounded-lg border-zinc-300 text-sm text-zinc-800 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]"><option>All members</option><option>Free plan members</option><option>Recently active members</option><option>Custom segment</option></select></label>
              <label><span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Daily budget</span><div class="relative mt-1.5"><span class="absolute left-3 top-2.5 text-sm text-zinc-400">$</span><input v-model="campaignDraft.budget" type="number" min="1" class="h-11 w-full rounded-lg border-zinc-300 pl-7 text-sm text-zinc-800 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]"></div></label>
              <label><span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Start date</span><input v-model="campaignDraft.startDate" type="date" class="mt-1.5 h-11 w-full rounded-lg border-zinc-300 text-sm text-zinc-800 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]"></label>
              <label><span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">End date</span><input v-model="campaignDraft.endDate" type="date" class="mt-1.5 h-11 w-full rounded-lg border-zinc-300 text-sm text-zinc-800 focus:border-[#2D8CFF] focus:ring-[#2D8CFF]"></label>
            </div>
            <div class="mt-6 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
              <div class="flex items-center justify-between"><span class="text-xs text-zinc-500">Campaign</span><span class="text-sm font-semibold text-zinc-900">{{ campaignDraft.name || 'Untitled campaign' }}</span></div>
              <div class="mt-3 flex items-center justify-between"><span class="text-xs text-zinc-500">Format & placement</span><span class="text-sm font-medium text-zinc-800">{{ campaignDraft.format }} · {{ campaignDraft.placement }}</span></div>
              <div class="mt-3 flex items-center justify-between"><span class="text-xs text-zinc-500">Estimated monthly spend</span><span class="text-sm font-semibold text-zinc-900">${{ estimatedMonthlySpend }}</span></div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between border-t border-zinc-200 bg-white px-6 py-4">
          <button type="button" class="h-10 rounded-lg px-4 text-sm font-semibold text-zinc-600 hover:bg-zinc-100" @click="builderStep === 1 ? closeCampaignBuilder() : builderStep--">
            {{ builderStep === 1 ? 'Cancel' : 'Back' }}
          </button>
          <button type="button" class="h-10 rounded-lg bg-[#2D8CFF] px-5 text-sm font-semibold text-white shadow-sm hover:bg-[#1f78e5] disabled:cursor-not-allowed disabled:opacity-40" :disabled="!canContinueBuilder" @click="builderStep < 3 ? builderStep++ : submitCampaign()">
            {{ builderStep < 3 ? 'Continue' : 'Submit for review' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="toastMessage" class="fixed bottom-6 right-6 z-[2700] flex max-w-sm items-center gap-3 rounded-xl bg-zinc-950 px-4 py-3 text-sm font-medium text-white shadow-xl">
      <CheckCircleIcon class="h-5 w-5 shrink-0 text-emerald-400" />
      {{ toastMessage }}
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhotoIcon,
  PlayIcon,
  PlusIcon,
  UsersIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';

const router = useRouter();
const accessLoading = ref(true);
const advertisingAccess = ref({ unlocked: false, onRequiredPlan: false, successfulCharges: 0, requiredSuccessfulCharges: 1, remainingSuccessfulCharges: 1, unlockPlan: null });
const unlockPlanName = computed(() => advertisingAccess.value?.unlockPlan?.name || 'a qualifying plan');
const accessRequirementTitle = computed(() => advertisingAccess.value.onRequiredPlan
  ? `${unlockPlanName.value} is active`
  : `Unlock with ${unlockPlanName.value}`);
const accessRequirementDetail = computed(() => {
  if (!advertisingAccess.value.onRequiredPlan) {
    const count = Number(advertisingAccess.value.requiredSuccessfulCharges || 1);
    return `Advertising unlocks after ${count} successful ${count === 1 ? 'plan charge' : 'plan charges'}.`;
  }
  const remaining = Number(advertisingAccess.value.remainingSuccessfulCharges || 0);
  return remaining > 0
    ? `Advertising unlocks after ${remaining} more successful ${remaining === 1 ? 'plan payment' : 'plan payments'}.`
    : 'Your Advertising workspace is ready.';
});

const loadAdvertisingAccess = async () => {
  accessLoading.value = true;
  try {
    const base = process.env.VUE_APP_API_PROXY_URL || '';
    const response = await fetch(`${base}/api/platforma-creator-billing/advertising-access`, { credentials: 'include', cache: 'no-store' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Unable to verify Advertising access');
    advertisingAccess.value = payload.access || advertisingAccess.value;
  } catch (error) {
    console.error('[Advertising] access check failed:', error);
    advertisingAccess.value = { ...advertisingAccess.value, unlocked: false };
  } finally {
    accessLoading.value = false;
  }
};

const openBilling = () => {
  const plan = advertisingAccess.value?.unlockPlan || null;
  const planId = Number.parseInt(String(plan?.id || ''), 10) || 0;
  const planSlug = String(plan?.slug || '').trim();
  if (!planId && !planSlug) {
    router.push('/creator/account/billing');
    return;
  }
  router.push({
    path: '/creator/account/billing',
    query: {
      ...(planId ? { upgradePlanId: String(planId) } : {}),
      ...(planSlug ? { upgradePlanSlug: planSlug } : {}),
      upgradeSource: 'advertising',
    },
  });
};

onMounted(loadAdvertisingAccess);

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'approvals', label: 'Approvals', count: 3 },
  { id: 'creatives', label: 'Creatives' },
  { id: 'placements', label: 'Placements' },
];

const activeTab = ref('overview');
const campaignSearch = ref('');
const campaignStatusFilter = ref('all');
const isCampaignBuilderOpen = ref(false);
const builderStep = ref(1);
const toastMessage = ref('');
let toastTimer = null;

const campaigns = ref([
  { id: 1, name: 'Creator Pro · August growth', status: 'Active', type: 'Traffic', format: 'Banner', placement: 'Member dashboard', budget: '$75/day', spend: '$1,248.40', impressions: '184,930', clicks: '6,472', ctr: '3.50%', leads: '382', ends: 'Aug 31' },
  { id: 2, name: 'Launch Week · Video teaser', status: 'Active', type: 'Awareness', format: 'Video', placement: 'Video pre-roll', budget: '$120/day', spend: '$2,106.84', impressions: '291,204', clicks: '7,108', ctr: '2.44%', leads: '—', ends: 'Sep 5' },
  { id: 3, name: 'Free members · Upgrade offer', status: 'In review', type: 'Conversions', format: 'Banner', placement: 'Newsfeed', budget: '$60/day', spend: '$0.00', impressions: '—', clicks: '—', ctr: '—', leads: '—', ends: 'Sep 18' },
  { id: 4, name: 'Course launch retargeting', status: 'Paused', type: 'Conversions', format: 'Video', placement: 'Course lesson', budget: '$90/day', spend: '$816.22', impressions: '98,401', clicks: '3,058', ctr: '3.11%', leads: '176', ends: 'No end date' },
  { id: 5, name: 'Podcast partner spotlight', status: 'Draft', type: 'Awareness', format: 'Banner', placement: 'Member dashboard', budget: '$40/day', spend: '$0.00', impressions: '—', clicks: '—', ctr: '—', leads: '—', ends: 'Not set' },
  { id: 6, name: 'Welcome flow sponsor', status: 'Ended', type: 'Traffic', format: 'Banner', placement: 'Member dashboard', budget: '$50/day', spend: '$1,492.70', impressions: '221,085', clicks: '5,814', ctr: '2.63%', leads: '294', ends: 'Aug 10' },
]);

const approvals = ref([
  { id: 101, name: 'Free members · Upgrade offer', advertiser: 'Platforma Creator Studio', format: 'Banner', brand: 'CREATOR PRO', headline: 'Build more. Earn more.', submitted: '18 minutes ago', placement: 'Newsfeed · In-feed', audience: 'Free members', budget: '$60/day' },
  { id: 102, name: 'Fall masterclass teaser', advertiser: 'Northstar Learning Co.', format: 'Video', brand: 'NORTHSTAR', headline: 'Your next skill starts here', submitted: '2 hours ago', placement: 'Video · Pre-roll', audience: 'All members', budget: '$85/day' },
  { id: 103, name: 'Community partner spotlight', advertiser: 'The Founders Circle', format: 'Banner', brand: 'FOUNDERS CIRCLE', headline: 'Meet your next collaborator.', submitted: 'Yesterday', placement: 'Member dashboard', audience: 'Active members', budget: '$45/day' },
]);

const creatives = [
  { name: 'creator-pro-wide-blue.png', type: 'Banner', size: '1200 × 480', used: 3, ctr: '3.50%', previewTitle: 'Create without limits.', previewClass: 'bg-gradient-to-br from-blue-700 to-cyan-400' },
  { name: 'launch-week-teaser.mp4', type: 'Video', size: '0:15 · 1080p', used: 2, ctr: '2.44%', previewTitle: '', previewClass: 'bg-gradient-to-br from-violet-700 via-fuchsia-600 to-orange-400' },
  { name: 'upgrade-member-card.png', type: 'Banner', size: '1080 × 1080', used: 1, ctr: '4.12%', previewTitle: 'Unlock the full experience.', previewClass: 'bg-gradient-to-br from-zinc-900 to-zinc-600' },
  { name: 'course-launch-pre-roll.mp4', type: 'Video', size: '0:30 · 1080p', used: 1, ctr: '3.11%', previewTitle: '', previewClass: 'bg-gradient-to-br from-emerald-700 to-teal-400' },
  { name: 'founders-circle-banner.png', type: 'Banner', size: '1200 × 480', used: 2, ctr: '2.86%', previewTitle: 'Your network, amplified.', previewClass: 'bg-gradient-to-br from-amber-600 to-rose-500' },
  { name: 'podcast-spotlight-square.png', type: 'Banner', size: '1080 × 1080', used: 1, ctr: '2.25%', previewTitle: 'Stories worth hearing.', previewClass: 'bg-gradient-to-br from-indigo-800 to-blue-500' },
];

const placements = reactive([
  { name: 'Member dashboard hero', detail: 'Large banner beneath the welcome header', icon: PhotoIcon, enabled: true, fillRate: '82%' },
  { name: 'Newsfeed in-feed', detail: 'Native ad after every 8 organic posts', icon: ChartBarIcon, enabled: true, fillRate: '76%' },
  { name: 'Video channel pre-roll', detail: 'Skippable video before member content', icon: VideoCameraIcon, enabled: true, fillRate: '64%' },
  { name: 'Course lesson sidebar', detail: 'Responsive banner beside lesson content', icon: PhotoIcon, enabled: false, fillRate: '—' },
  { name: 'Post-login interstitial', detail: 'Full-screen creative, frequency capped', icon: EyeIcon, enabled: false, fillRate: '—' },
]);

const overviewMetrics = [
  { label: 'Ad spend', value: '$5,664.16', change: '+12.8%', context: 'vs. previous 30 days', positive: true, icon: CurrencyDollarIcon },
  { label: 'Impressions', value: '795.6K', change: '+18.2%', context: 'vs. previous 30 days', positive: true, icon: EyeIcon },
  { label: 'Clicks', value: '22,452', change: '2.82% CTR', context: 'across all placements', positive: true, icon: ChartBarIcon },
  { label: 'Conversions', value: '852', change: '$6.65', context: 'average cost per conversion', positive: false, icon: UsersIcon },
];

const attentionItems = [
  { title: '3 campaigns need approval', detail: 'Review creative and targeting before launch.', icon: ClockIcon, iconClass: 'bg-amber-50 text-amber-600', tab: 'approvals' },
  { title: '1 campaign ends soon', detail: 'Creator Pro · August growth ends in 12 days.', icon: ExclamationTriangleIcon, iconClass: 'bg-rose-50 text-rose-600', tab: 'campaigns' },
  { title: 'Course sidebar is disabled', detail: 'Enable this placement to unlock more inventory.', icon: MapPinIcon, iconClass: 'bg-blue-50 text-[#2D8CFF]', tab: 'placements' },
];

const builderSteps = ['Goal', 'Creative', 'Delivery'];
const goals = [
  { id: 'awareness', label: 'Brand awareness', detail: 'Maximize impressions and reach across member accounts.', icon: EyeIcon },
  { id: 'traffic', label: 'Website traffic', detail: 'Drive qualified members to a page or external website.', icon: ChartBarIcon },
  { id: 'conversions', label: 'Conversions', detail: 'Optimize for purchases, registrations, or upgrades.', icon: CurrencyDollarIcon },
  { id: 'engagement', label: 'Member engagement', detail: 'Encourage views, reactions, and content discovery.', icon: UsersIcon },
];

const createEmptyDraft = () => ({ goal: 'traffic', name: '', format: 'Banner', placement: 'Member dashboard', audience: 'All members', budget: 50, startDate: '2026-08-20', endDate: '2026-09-20' });
const campaignDraft = reactive(createEmptyDraft());

const filteredCampaigns = computed(() => {
  const query = campaignSearch.value.trim().toLowerCase();
  return campaigns.value.filter((campaign) => {
    const matchesQuery = !query || [campaign.name, campaign.type, campaign.format, campaign.placement].some((value) => String(value).toLowerCase().includes(query));
    const matchesStatus = campaignStatusFilter.value === 'all' || campaign.status === campaignStatusFilter.value;
    return matchesQuery && matchesStatus;
  });
});

const estimatedMonthlySpend = computed(() => (Number(campaignDraft.budget || 0) * 30).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const canContinueBuilder = computed(() => {
  if (builderStep.value === 1) return Boolean(campaignDraft.goal && campaignDraft.name.trim());
  if (builderStep.value === 2) return Boolean(campaignDraft.format && campaignDraft.placement);
  return Boolean(campaignDraft.audience && Number(campaignDraft.budget) > 0 && campaignDraft.startDate && campaignDraft.endDate);
});

const showToast = (message) => {
  toastMessage.value = message;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { toastMessage.value = ''; }, 3200);
};

const openCampaignBuilder = () => {
  Object.assign(campaignDraft, createEmptyDraft());
  builderStep.value = 1;
  isCampaignBuilderOpen.value = true;
};

const closeCampaignBuilder = () => { isCampaignBuilderOpen.value = false; };

const submitCampaign = () => {
  const newCampaign = {
    id: Date.now(), name: campaignDraft.name.trim(), status: 'In review', type: goals.find((goal) => goal.id === campaignDraft.goal)?.label || 'Traffic', format: campaignDraft.format, placement: campaignDraft.placement,
    budget: `$${Number(campaignDraft.budget).toFixed(0)}/day`, spend: '$0.00', impressions: '—', clicks: '—', ctr: '—', leads: '—', ends: new Date(`${campaignDraft.endDate}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  };
  campaigns.value.unshift(newCampaign);
  approvals.value.unshift({ id: newCampaign.id, name: newCampaign.name, advertiser: 'Platforma Creator Studio', format: newCampaign.format, brand: 'NEW CAMPAIGN', headline: newCampaign.name, submitted: 'just now', placement: newCampaign.placement, audience: campaignDraft.audience, budget: newCampaign.budget });
  closeCampaignBuilder();
  activeTab.value = 'campaigns';
  showToast('Campaign submitted for approval.');
};

const toggleCampaign = (id) => {
  const campaign = campaigns.value.find((item) => item.id === id);
  if (!campaign || ['In review', 'Draft', 'Ended'].includes(campaign.status)) return;
  campaign.status = campaign.status === 'Active' ? 'Paused' : 'Active';
  showToast(`Campaign ${campaign.status === 'Active' ? 'activated' : 'paused'}.`);
};

const approveCampaign = (id) => {
  const approval = approvals.value.find((item) => item.id === id);
  approvals.value = approvals.value.filter((item) => item.id !== id);
  const campaign = campaigns.value.find((item) => item.id === id || item.name === approval?.name);
  if (campaign) campaign.status = 'Active';
  showToast(`${approval?.name || 'Campaign'} approved and scheduled.`);
};

const requestChanges = (id) => {
  const approval = approvals.value.find((item) => item.id === id);
  showToast(`Change request opened for ${approval?.name || 'campaign'}.`);
};

const statusClass = (status) => ({
  Active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  'In review': 'bg-amber-50 text-amber-700 ring-amber-600/20',
  Paused: 'bg-zinc-100 text-zinc-600 ring-zinc-500/20',
  Draft: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  Ended: 'bg-rose-50 text-rose-700 ring-rose-600/20',
}[status] || 'bg-zinc-100 text-zinc-600 ring-zinc-500/20');

const CampaignTable = defineComponent({
  name: 'CampaignTable',
  props: { campaigns: { type: Array, default: () => [] }, compact: Boolean, selectable: Boolean },
  emits: ['toggle'],
  setup(props, { emit }) {
    return () => h('div', { class: 'overflow-x-auto' }, [
      h('table', { class: 'min-w-[1120px] w-full border-collapse' }, [
        h('thead', { class: 'bg-zinc-50' }, [h('tr', {}, [
          props.selectable ? h('th', { class: 'w-10 px-4 py-3' }, [h('input', { type: 'checkbox', class: 'rounded border-zinc-300 text-[#2D8CFF]' })]) : null,
          ...['Campaign', 'Status', 'Format', 'Placement', 'Budget', 'Spend', 'Impressions', 'Clicks', 'CTR', 'Conversions', 'Ends', ''].map((label) => h('th', { class: 'whitespace-nowrap px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-500' }, label)),
        ])]),
        h('tbody', { class: 'divide-y divide-zinc-100 bg-white' }, props.campaigns.map((campaign) => h('tr', { key: campaign.id, class: 'group hover:bg-zinc-50/70' }, [
          props.selectable ? h('td', { class: 'px-4 py-3' }, [h('input', { type: 'checkbox', class: 'rounded border-zinc-300 text-[#2D8CFF]' })]) : null,
          h('td', { class: 'max-w-[260px] px-4 py-3' }, [h('button', { type: 'button', class: 'block truncate text-left text-sm font-semibold text-[#2377d9] hover:underline' }, campaign.name), h('span', { class: 'mt-0.5 block text-[11px] text-zinc-400' }, campaign.type)]),
          h('td', { class: 'px-4 py-3' }, [h('span', { class: ['inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ring-1 ring-inset', statusClass(campaign.status)] }, campaign.status)]),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-600' }, campaign.format),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-600' }, campaign.placement),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs font-medium text-zinc-800' }, campaign.budget),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-700' }, campaign.spend),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-700' }, campaign.impressions),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-700' }, campaign.clicks),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-700' }, campaign.ctr),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-700' }, campaign.leads),
          h('td', { class: 'whitespace-nowrap px-4 py-3 text-xs text-zinc-500' }, campaign.ends),
          h('td', { class: 'px-4 py-3' }, [h('div', { class: 'flex items-center justify-end gap-2' }, [
            ['Active', 'Paused'].includes(campaign.status) ? h('button', { type: 'button', class: ['relative h-5 w-9 rounded-full transition', campaign.status === 'Active' ? 'bg-[#2D8CFF]' : 'bg-zinc-300'], onClick: () => emit('toggle', campaign.id) }, [h('span', { class: ['absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition', campaign.status === 'Active' ? 'left-[18px]' : 'left-0.5'] })]) : null,
            h('button', { type: 'button', class: 'rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700' }, [h(EllipsisHorizontalIcon, { class: 'h-5 w-5' })]),
          ])]),
        ]))),
      ]),
    ]);
  },
});
</script>
